import React, { useState, useRef, useCallback, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Search, ChevronDown } from 'lucide-react';
import './FullCalendarReact.css';

const EVENT_PRESETS = [
    { bg: '#dbeafe', text: '#1e40af', tagBg: 'rgba(30,64,175,0.12)' },
    { bg: '#fef3c7', text: '#92400e', tagBg: 'rgba(146,64,14,0.12)' },
    { bg: '#d1fae5', text: '#065f46', tagBg: 'rgba(6,95,70,0.12)' },
    { bg: '#ede9fe', text: '#5b21b6', tagBg: 'rgba(91,33,182,0.12)' },
    { bg: '#fee2e2', text: '#991b1b', tagBg: 'rgba(153,27,27,0.12)' },
    { bg: '#e0f2fe', text: '#0c4a6e', tagBg: 'rgba(12,74,110,0.12)' },
    { bg: '#fce7f3', text: '#9d174d', tagBg: 'rgba(157,23,77,0.12)' },
    { bg: '#fff7ed', text: '#9a3412', tagBg: 'rgba(154,52,18,0.12)' },
];

function getPreset(index) {
    return EVENT_PRESETS[index % EVENT_PRESETS.length];
}

const SAMPLE_EVENTS = (() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);

    const makeDate = (dayOffset, hour, minute = 0) => {
        const d = new Date(monday);
        d.setDate(d.getDate() + dayOffset);
        d.setHours(hour, minute, 0, 0);
        return d.toISOString();
    };

    return [
        { id: '1', title: 'Team Standup', start: makeDate(0, 7, 0), end: makeDate(0, 7, 45), location: 'Meeting Room A • 8 attendees', tag: 'Daily', colorIndex: 0 },
        { id: '2', title: 'Client Review', start: makeDate(0, 10, 0), end: makeDate(0, 11, 30), location: 'Conference Hall • 12 attendees', tag: 'Priority', colorIndex: 1 },
        { id: '3', title: 'Sprint Planning', start: makeDate(0, 14, 0), end: makeDate(0, 15, 30), location: 'Virtual • 18 joined', tag: 'Recurring', colorIndex: 4 },
        { id: '4', title: 'Design Review', start: makeDate(1, 8, 0), end: makeDate(1, 9, 0), location: 'Studio B • 6 attendees', tag: 'Design', colorIndex: 3 },
        { id: '5', title: 'Product Demo', start: makeDate(2, 7, 0), end: makeDate(2, 8, 0), location: 'Main Stage • 20 booked', tag: 'New Feature', colorIndex: 2 },
        { id: '6', title: 'Code Review', start: makeDate(2, 12, 30), end: makeDate(2, 13, 30), location: 'Dev Lab • 5 attendees', tag: 'Engineering', colorIndex: 5 },
        { id: '7', title: 'Workshop', start: makeDate(3, 7, 0), end: makeDate(3, 7, 30), location: 'Arena • 25 booked', tag: 'Training', colorIndex: 0 },
        { id: '8', title: 'Strategy Session', start: makeDate(3, 13, 15), end: makeDate(3, 13, 45), location: 'Board Room • 8 attendees', tag: 'Executive', colorIndex: 6 },
        { id: '9', title: 'Retrospective', start: makeDate(4, 8, 0), end: makeDate(4, 9, 0), location: 'Studio C • 10 attendees', tag: 'Sprint', colorIndex: 7 },
        { id: '10', title: 'Team Building', start: makeDate(4, 17, 0), end: makeDate(4, 18, 0), location: 'Outdoor • 22 booked', tag: 'Team Event', colorIndex: 1 },
        { id: '11', title: 'Performance Review', start: makeDate(5, 9, 0), end: makeDate(5, 10, 30), location: 'HR Office • 4 attendees', tag: 'HR', colorIndex: 3 },
        { id: '12', title: 'Tech Talk', start: makeDate(5, 17, 30), end: makeDate(5, 18, 30), location: 'Auditorium • 30 booked', tag: 'Knowledge', colorIndex: 2 },
        { id: '13', title: 'Open Session', start: makeDate(6, 8, 30), end: makeDate(6, 10, 0), location: 'All zones • Capacity 60', tag: 'Open', colorIndex: 5 },
        { id: '14', title: 'Community Call', start: makeDate(6, 16, 30), end: makeDate(6, 17, 30), location: 'Virtual • 40 invited', tag: 'Community', colorIndex: 7 },
    ];
})();

function FullCalendarReact({ initialEvents = [] }) {
    const [events, setEvents] = useState(initialEvents);

    useEffect(() => {
        setEvents(initialEvents);
    }, [initialEvents]);
    const [currentView, setCurrentView] = useState('timeGridWeek');
    const [dateTitle, setDateTitle] = useState('');
    const [weekRange, setWeekRange] = useState('');
    const [monthYear, setMonthYear] = useState('');
    const calendarRef = useRef(null);

    const updateTitles = useCallback(() => {
        const api = calendarRef.current?.getApi();
        if (!api) return;
        setDateTitle(api.view.title);

        const start = api.view.activeStart;
        const end = api.view.activeEnd;
        const endDisplay = new Date(end);
        endDisplay.setDate(endDisplay.getDate() - 1);

        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        setMonthYear(`${monthNames[start.getMonth()]} ${start.getFullYear()}`);

        const startDay = start.getDate();
        const endDay = endDisplay.getDate();
        const startMonth = monthNames[start.getMonth()].substring(0, 3);
        const endMonth = monthNames[endDisplay.getMonth()].substring(0, 3);
        setWeekRange(`${startMonth} ${startDay} – ${endMonth} ${endDay}`);
    }, []);

    useEffect(() => {
        // Run once after mount to populate titles immediately
        const timer = setTimeout(() => {
            updateTitles();
        }, 100);
        return () => clearTimeout(timer);
    }, [updateTitles]);

    const handleViewChange = (viewName) => {
        setCurrentView(viewName);
        const api = calendarRef.current?.getApi();
        if (api) {
            api.changeView(viewName);
            setTimeout(updateTitles, 50);
        }
    };

    const handlePrev = () => {
        const api = calendarRef.current?.getApi();
        if (api) { api.prev(); setTimeout(updateTitles, 50); }
    };

    const handleNext = () => {
        const api = calendarRef.current?.getApi();
        if (api) { api.next(); setTimeout(updateTitles, 50); }
    };

    const handleToday = () => {
        const api = calendarRef.current?.getApi();
        if (api) { api.today(); setTimeout(updateTitles, 50); }
    };

    const handleDateClick = (info) => {
        const title = prompt('Enter appointment name:');
        if (title) {
            const colorIndex = Math.floor(Math.random() * EVENT_PRESETS.length);
            const startDate = new Date(info.dateStr);
            const endDate = new Date(startDate);
            endDate.setMinutes(endDate.getMinutes() + 60);

            setEvents(prev => [...prev, {
                id: String(Date.now()),
                title,
                start: startDate.toISOString(),
                end: endDate.toISOString(),
                location: 'New appointment',
                tag: 'Appointment',
                colorIndex,
            }]);
        }
    };

    const handleEventClick = (info) => {
        if (window.confirm(`Delete "${info.event.title}"?`)) {
            setEvents(prev => prev.filter(e => e.id !== info.event.id));
        }
    };

    const handleEventDrop = (info) => {
        setEvents(prev => prev.map(e => {
            if (e.id === info.event.id) {
                return { ...e, start: info.event.startStr, end: info.event.endStr };
            }
            return e;
        }));
    };

    const renderEventContent = (eventInfo) => {
        const extProps = eventInfo.event.extendedProps;
        const colorIndex = extProps.colorIndex || 0;
        const preset = getPreset(colorIndex);

        const startTime = eventInfo.event.start;
        const endTime = eventInfo.event.end;
        let durationMin = 0;
        if (startTime && endTime) {
            durationMin = Math.round((endTime - startTime) / 60000);
        }

        const timeStr = startTime ? startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : '';

        return (
            <div className="schedule-event-card" style={{ color: preset.text }}>
                <div className="schedule-event-header">
                    <span className="schedule-event-title">{eventInfo.event.title}</span>
                    <span className="schedule-event-time">{timeStr} • {durationMin} min</span>
                </div>
                {extProps.location && (
                    <div className="schedule-event-location">{extProps.location}</div>
                )}
                {extProps.tag && (
                    <span className="schedule-event-tag" style={{ background: preset.tagBg }}>
                        {extProps.tag}
                    </span>
                )}
            </div>
        );
    };

    const calendarEvents = events.map(e => ({
        id: e.id,
        title: e.title,
        start: e.start,
        end: e.end,
        backgroundColor: getPreset(e.colorIndex || 0).bg,
        borderColor: 'transparent',
        textColor: getPreset(e.colorIndex || 0).text,
        extendedProps: {
            location: e.location,
            tag: e.tag,
            colorIndex: e.colorIndex,
        }
    }));

    return (
        <div className="schedule-calendar-card">
            {/* Header */}
            <div className="schedule-header">
                <div className="schedule-header-top">
                    <div className="schedule-header-left">
                        <div className="schedule-label">Schedule</div>
                        <h4>Appointment Calendar</h4>
                        <p className="schedule-subtitle">Plan, track, and manage all appointments</p>
                    </div>
                    <div className="schedule-header-right">
                        <div className="schedule-date-badge" onClick={handleToday}>
                            {monthYear || 'Loading...'}
                            <ChevronDown size={14} />
                        </div>
                        <div className="schedule-date-badge">
                            {weekRange}
                        </div>
                        <div className="schedule-view-switcher">
                            <button className={`schedule-view-btn ${currentView === 'timeGridDay' ? 'active' : ''}`}
                                    onClick={() => handleViewChange('timeGridDay')}>Day</button>
                            <button className={`schedule-view-btn ${currentView === 'timeGridWeek' ? 'active' : ''}`}
                                    onClick={() => handleViewChange('timeGridWeek')}>Week</button>
                            <button className={`schedule-view-btn ${currentView === 'dayGridMonth' ? 'active' : ''}`}
                                    onClick={() => handleViewChange('dayGridMonth')}>Month</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sub Header */}
            <div className="schedule-sub-header">
                <div className="schedule-sub-left">
                    <button className="schedule-month-select" onClick={handlePrev}>
                        ◀
                    </button>
                    <span className="schedule-week-range">{dateTitle}</span>
                    <button className="schedule-month-select" onClick={handleNext}>
                        ▶
                    </button>
                    <button className="schedule-today-btn" onClick={handleToday}>
                        Today
                    </button>
                </div>
                <div className="schedule-sub-right">
                    <div className="schedule-search">
                        <Search size={14} />
                        <input type="text" placeholder="Search appointment" />
                    </div>
                </div>
            </div>

            {/* Calendar Body */}
            <div className="schedule-calendar-body">
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    editable={true}
                    selectable={true}
                    droppable={true}
                    events={calendarEvents}
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    eventDrop={handleEventDrop}
                    eventResize={handleEventDrop}
                    eventContent={renderEventContent}
                    headerToolbar={false}
                    height="calc(100vh - 310px)"
                    slotMinTime="00:00:00"
                    slotMaxTime="24:00:00"
                    slotDuration="01:00:00"
                    dayMaxEvents={3}
                    nowIndicator={true}
                    allDaySlot={false}
                    firstDay={1}
                    dayHeaderFormat={{ weekday: 'short', day: 'numeric', month: 'short' }}
                    slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                    datesSet={updateTitles}
                    eventDisplay="block"
                />
            </div>
        </div>
    );
}

export default FullCalendarReact;
